import Logger from '../lib/logger';

// -----------------------------------------------------------------------------
// CartGifts Web Component
// -----------------------------------------------------------------------------
// Provides "Gift With Purchase" functionality entirely via JS—no Liquid markup needed.
// Global toggle allows enabling/disabling the entire module.
//
// PERFORMANCE NOTE: Current implementation uses immediate processing for removals
// with optimistic UI updates. For even faster performance (zero delay), consider
// implementing fetch interception to merge gift updates into the original cart
// request atomically. See implementation notes below.
//
// FUTURE OPTIMIZATION (Fetch Interception Approach):
// Intercept fetch requests to /cart/update.js and /cart/add.js, calculate needed
// gift updates, and merge them into the original request body. This makes gift
// updates part of the atomic cart operation with zero additional delay.
// Example: When user removes trigger variant, the removal request + gift removal
// happen in a single network call instead of two sequential calls.

/**
 * Global on/off switch for the Gift With Purchase module.
 * Set to false to completely disable GWP behavior, hide UI, and remove gift SKUs.
 */
const GWP_ENABLED = true;

/**
 * Trigger modes for Gift With Purchase
 * @readonly
 * @enum {string}
 */
const GWP_MODES = {
	THRESHOLD: 'threshold',           // Spend threshold trigger
	PRODUCT_IN_CART: 'productInCart', // Product-in-cart trigger
};

/**
 * Configuration for each gift slot.
 */
const GIFT_CONFIGS = [
	{
		handle: 'betterdays-frother-free-gift',    // Shopify product handle for gift #1
		mode: GWP_MODES.PRODUCT_IN_CART,              // Unlock when specific product is in cart
		triggerVariantId: [
			46737754161451, // BetterHormones Non 30-Stick
			46737701470507, // BetterHormones Caff 30-Stick
			52580638359915, // BetterHormones Caff Cola
			46760800485675, // BetterHormones Non SCP Peach Kiwi
			46760800420139, // BetterHormones Non SCP Pineapple Coconut
			52605642834283, // BetterHormones Non SCP Strawberry Lemonade
		],             // Variant ID that triggers this gift
		triggerProductTitle: 'BetterHormones', // Display name for user-facing message
		enabled: true,                                // Toggle this gift on/off
	},
	// {
	// 	handle: 'onthego-box-gwp',                    // Shopify product handle for gift #2
	// 	mode: GWP_MODES.THRESHOLD,                    // Unlock when spend threshold reached
	// 	threshold: 25000,                             // Threshold in cents (e.g. $250.00)
	// 	enabled: false,                                // Toggle this gift on/off
	// },
];

/**
 * Represents a Gift With Purchase product and its eligibility logic.
 */
class Gift {
	/**
	 * @param {object} config
	 */
	constructor({ handle, mode, triggerVariantId = null, threshold = null, enabled = true, triggerProductTitle = '' }) {
		this.handle = handle;
		this.mode = mode;
		this.triggerVariantId = triggerVariantId;
		this.threshold = threshold;
		this.configEnabled = enabled;
		this.triggerProductTitle = triggerProductTitle;
		this.enabled = false;
		this.inCart = false;
	}

	/**
	 * Load product data and set config validity.
	 */
	async load() {
		try {
			const url = `${window.Shopify.routes.root}products/${this.handle}.js`;
			const resp = await fetch(url);
			if (!resp.ok) throw new Error(`Failed to load product: ${this.handle}`);
			const data = await resp.json();
			this.title = data.title;
			this.variants = data.variants;
			this.giftVariantId = data.variants[0].id;
			this.featuredImage = data.featured_image;
			if (this.mode === GWP_MODES.THRESHOLD && this.threshold == null) {
				this.threshold = data.threshold || 0;
			}
			const configValid = (
				(this.mode === GWP_MODES.THRESHOLD && this.threshold > 0) ||
				(this.mode === GWP_MODES.PRODUCT_IN_CART && this.triggerVariantId)
			);
			this.enabled = this.configEnabled && configValid;
		} catch (err) {
			Logger.error('CartGifts > Gift.load', err);
			this.enabled = false;
		}
	}

	/**
	 * Update inCart status based on cart data.
	 */
	updateCartStatus(cartData) {
		this.inCart = !!cartData.items.find(item => item.id === this.giftVariantId);
	}

	/**
	 * Check if the gift is eligible based on trigger mode.
	 */
	isEligible(cartData) {
		if (!this.enabled) return false;
		if (this.mode === GWP_MODES.THRESHOLD) {
			return cartData.original_total_price >= this.threshold;
		}
		if (this.mode === GWP_MODES.PRODUCT_IN_CART) {

			// If this.triggerVariantId is an array, check if any of the variants are in the cart
			if (Array.isArray(this.triggerVariantId)) {
				return this.triggerVariantId.some(id => cartData.items.find(item => item.id === id));
			}
			// Otherwise, check if the specific variant is in the cart
			return !!cartData.items.find(item => item.id === this.triggerVariantId);
		}
		return false;
	}
}

/**
 * Core logic for CartGifts: inject UI, monitor cart, and sync gifts.
 */
class CartGiftsLogic {
	constructor(rootEl) {
		this.container = rootEl;
		this.gifts = GIFT_CONFIGS.map(cfg => new Gift(cfg));
		this.cartData = null;
		this.productTitleEls = null;
		this.imageEl = null;
		this.headlineEl = null;
		this.instructionsEl = null;
		this.syncInProgress = false;  // Lock flag to prevent concurrent syncs
		this.pendingSync = false;     // Queue flag for pending sync operations
		this.debounceTimer = null;    // Debounce timer for rapid cart changes
		this.cartDataTimestamp = null;  // Track when cart data was last fetched
		this.isProgrammaticReload = false;  // Flag to skip navigation guard during our own reloads
	}

	/**
	 * Initialize component: handle global toggle, inject UI, load gifts, sync cart.
	 */
	async init() {
		// If globally disabled, hide and remove gift SKUs
		if (!GWP_ENABLED) {
			this.container.style.display = 'none';
			await Promise.all(this.gifts.map(g => g.load()));
			this.cartData = await this.fetchCart();
			if (this.cartData) {
				const updates = {};
				this.gifts.forEach(g => {
					g.updateCartStatus(this.cartData);
					if (g.inCart) updates[g.giftVariantId] = 0;
				});
				if (Object.keys(updates).length) {
					await fetch('/cart/update.js', {
						method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
					});
				}
			}
			return;
		}

		// Inject banner HTML if missing
		if (!this.container.querySelector('.cart-gifts__banner')) {
			this.container.innerHTML = `
        <div class="cart-gifts__banner">
          <h3></h3>
          <div class="cart-gifts__meter"><div class="cart-gifts__meter__line"></div></div>
          <div class="cart-gifts__locked-item">
            <div class="cart-gifts__locked-item__image"><img src="" width="63" height="96" loading="lazy" /></div>
            <div class="cart-gifts__locked-item__content">
              <div class="cart-gifts__locked-item__content__title">
                <h4 class="cart-gifts__product-title"></h4>
                <p class="cart-gifts__instructions"></p>
              </div>
              <div class="cart-gifts__locked-item__content__locked">Locked</div>
            </div>
          </div>
        </div>`;
		}

		// Cache UI elements
		this.productTitleEls = this.container.querySelectorAll('.cart-gifts__product-title');
		this.imageEl = this.container.querySelector('.cart-gifts__locked-item__image img');
		this.headlineEl = this.container.querySelector('h3');
		this.instructionsEl = this.container.querySelector('.cart-gifts__instructions');

		// Load gift data and perform initial sync
		await Promise.all(this.gifts.map(g => g.load()));

		// Setup navigation guard
		this.setupNavigationGuard();

		await this.onCartChange();
	}

	/** Fetch current cart data. */
	async fetchCart() {
		const resp = await fetch('/cart.js');
		return resp.ok ? resp.json() : null;
	}

	/**
	 * Handle cart updates: refresh UI and sync gifts.
	 * Now processes removals immediately with optimistic updates for zero delay.
	 * Accepts optional cart data from event to avoid redundant fetches.
	 */
	async onCartChange(eventCartData = null) {
		if (!GWP_ENABLED) return;

		// Use provided cart data if available, otherwise fetch
		let cartData;
		if (eventCartData) {
			cartData = eventCartData;
			this.cartDataTimestamp = Date.now();
		} else {
			// For non-event calls, still fetch (but this should be rare)
			cartData = await this.fetchCart();
			this.cartDataTimestamp = Date.now();
		}

		if (!cartData) return;

		// Check if this is a removal (fewer items than before)
		const isRemoval = this.cartData && cartData.items.length < this.cartData.items.length;

		// Update local state immediately
		this.cartData = cartData;
		this.gifts.forEach(g => g.updateCartStatus(cartData));

		// For removals, process IMMEDIATELY with no debounce
		if (isRemoval) {
			// Clear any pending debounce
			if (this.debounceTimer) {
				clearTimeout(this.debounceTimer);
				this.debounceTimer = null;
			}

			// Update UI optimistically (immediate visual feedback)
			this.renderBanner();

			// Process sync immediately using microtask (faster than setTimeout)
			await Promise.resolve(); // Yield to current execution, then continue
			await this.syncCart();
		} else {
			// For additions, use minimal debounce (50ms) to batch rapid adds
			if (this.debounceTimer) {
				clearTimeout(this.debounceTimer);
			}

			// Update UI optimistically
			this.renderBanner();

			this.debounceTimer = setTimeout(async () => {
				this.debounceTimer = null;
				await this.syncCart();
			}, 50);
		}
	}

	/**
	 * Render banner state: next gift or final unlocked message.
	 */
	renderBanner() {
		const enabledGifts = this.gifts.filter(g => g.enabled);
		const totalEnabled = enabledGifts.length;
		const unlockedCount = enabledGifts.filter(g => g.inCart).length;
		const nextGift = enabledGifts.find(g => !g.isEligible(this.cartData));

		if (totalEnabled === 0) { this.container.style.display = 'none'; return; }
		if (unlockedCount >= totalEnabled) {
			// Hide the locked-item section when no more gifts remain
			const lockedItemEl = this.container.querySelector('.cart-gifts__locked-item');
			if (lockedItemEl) lockedItemEl.style.display = 'none';
			// Fill the progress meter to 100%
			const meterLine = this.container.querySelector('.cart-gifts__meter__line');
			if (meterLine) meterLine.style.width = '100%';
			// Display singular or plural success message
			if (totalEnabled === 1) {
				this.headlineEl.textContent = `You've unlocked your free gift!`;
			} else {
				this.headlineEl.textContent = `You've unlocked all ${totalEnabled} free gifts!`;
			}
			this.instructionsEl.textContent = '';
			this.container.style.display = '';
			return;
		}
		if (nextGift) {
			const titleText = nextGift.title.replace(' (Free Gift)', '');
			const imgUrl = nextGift.featuredImage ? `${nextGift.featuredImage}&width=126` : '';
			this.imageEl.src = imgUrl;
			this.productTitleEls.forEach(el => el.textContent = titleText);
			const prefix = unlockedCount ? `You've unlocked ${unlockedCount} gift${unlockedCount > 1 ? 's' : ''}! Next up:` : '';
			if (nextGift.mode === GWP_MODES.THRESHOLD) {
				const spent = this.cartData.original_total_price;
				const threshold = nextGift.threshold;
				const remaining = ((threshold - spent) / 100).toFixed(2);
				const pct = Math.min((spent / threshold) * 100, 100) + '%';
				this.headlineEl.innerHTML = `${prefix} You're $<span class="cart-gifts__dollars-away">${remaining}</span> away from a free <span class="cart-gifts__product-title">${titleText}</span>!`;
				this.instructionsEl.innerHTML = `Spend $<span class="cart-gifts__threshold">${(threshold / 100).toFixed(0)}</span> more to unlock it.`;
				this.container.querySelector('.cart-gifts__meter__line').style.width = pct;
			} else {
				this.headlineEl.innerHTML = `${prefix} Get a free <span class="cart-gifts__product-title">${titleText}</span> with your order!`;
				this.instructionsEl.textContent = `Add ${nextGift.triggerProductTitle} to unlock this gift.`;
			}
			this.container.style.display = '';
		}
	}

	/**
	 * Disable/enable checkout buttons during critical sync operations.
	 */
	setCheckoutButtonsDisabled(disabled) {
		const checkoutButtons = document.querySelectorAll(
			'button[name="checkout"], .cart__submit, .quick-cart__submit, [data-checkout], .purchase-confirmation-popup__submit'
		);

		checkoutButtons.forEach(btn => {
			if (disabled) {
				btn.disabled = true;
				btn.setAttribute('aria-busy', 'true');
				// Store original text if not already stored
				if (!btn.dataset.originalText) {
					btn.dataset.originalText = btn.textContent.trim();
				}
				btn.textContent = 'Processing...';
			} else {
				btn.disabled = false;
				btn.removeAttribute('aria-busy');
				// Restore original text if stored
				if (btn.dataset.originalText) {
					btn.textContent = btn.dataset.originalText;
					delete btn.dataset.originalText;
				}
			}
		});
	}

	/**
	 * Setup navigation guard to warn users if they try to leave during sync.
	 * Only warns for actual user navigation, not programmatic reloads.
	 */
	setupNavigationGuard() {
		if (!GWP_ENABLED) return;

		this._beforeUnloadHandler = (e) => {
			// Don't warn if it's our own programmatic reload or if sync isn't in progress
			if (this.syncInProgress && !this.isProgrammaticReload) {
				// Modern browsers ignore custom messages, but still show warning
				e.preventDefault();
				e.returnValue = ''; // Required for Chrome
				return ''; // Required for some browsers
			}
		};

		window.addEventListener('beforeunload', this._beforeUnloadHandler);
	}

	/**
	 * Sync gift SKUs in cart via /cart/update.js call.
	 * Optimized for speed - uses response data instead of re-fetching.
	 */
	async syncCart() {
		// Prevent concurrent syncs
		if (this.syncInProgress) {
			this.pendingSync = true;
			return;
		}

		this.syncInProgress = true;

		try {
			// Use the cart data we already have (it's fresh from onCartChange)
			const latestCart = this.cartData;
			if (!latestCart) {
				this.syncInProgress = false;
				return;
			}

			// Update gift status
			this.gifts.forEach(g => g.updateCartStatus(latestCart));

			const updates = {};
			this.gifts.forEach(g => {
				const shouldHave = g.isEligible(latestCart);
				if (shouldHave && !g.inCart) updates[g.giftVariantId] = 1;
				if (!shouldHave && g.inCart) updates[g.giftVariantId] = 0;
			});

			if (Object.keys(updates).length) {
				// Disable checkout buttons during critical sync
				this.setCheckoutButtonsDisabled(true);

				try {
					// Make the update request with keepalive flag
					// This ensures the request completes even if user navigates away
					const resp = await fetch('/cart/update.js', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ updates }),
						keepalive: true  // Request continues even if page unloads
					});

					if (resp.ok) {
						// Get updated cart from response (Shopify returns cart in response)
						const updatedCart = await resp.json();
						if (updatedCart && updatedCart.items) {
							this.cartData = updatedCart;
							this.cartDataTimestamp = Date.now();
							this.gifts.forEach(g => g.updateCartStatus(this.cartData));
							this.renderBanner();
						}

						// Refresh UI
						const section = window.Shopify.theme.sections.instances.find(s => s.refreshQuickCart);
						if (section) section.refreshQuickCart();
						if (location.pathname === '/cart') {
							// Set flag to skip navigation guard during our own reload
							this.isProgrammaticReload = true;
							location.reload();
						}
					}
				} finally {
					// Always re-enable buttons, even if request fails
					this.setCheckoutButtonsDisabled(false);
				}
			}
		} finally {
			this.syncInProgress = false;

			// Process queued sync using microtask for faster processing
			if (this.pendingSync) {
				this.pendingSync = false;
				Promise.resolve().then(() => this.syncCart());
			}
		}
	}
}

/**
 * Custom element <cart-gifts>
 */
export default class CartGifts extends HTMLElement {
	constructor() {
		super();
		this.logic = new CartGiftsLogic(this);
		this._onCartUpdate = (e) => {
			// Pass cart data from event if available
			const cartData = e?.detail?.cart || null;
			this.logic.onCartChange(cartData);
		};
		this._onRemoveClick = e => {
			if (e.target.matches('.cart__remove, [data-cart-remove], .cart__quantity input')) {
				this.logic.onCartChange();
			}
		};
	}

	connectedCallback() {
		// Always run initialization, including removal logic if disabled
		this.logic.init();

		// If globally disabled, hide UI and skip event listeners (gifts already removed)
		if (!GWP_ENABLED) {
			this.style.display = 'none';
			return;
		}

		// Module enabled: set up cart event listeners and polling
		document.addEventListener('cart:updated', this._onCartUpdate);
		document.addEventListener('cart:refresh', this._onCartUpdate);
		document.addEventListener('click', this._onRemoveClick);
		this._pollInterval = setInterval(() => this.logic.onCartChange(), 2000);
	}

	disconnectedCallback() {
		document.removeEventListener('cart:updated', this._onCartUpdate);
		document.removeEventListener('cart:refresh', this._onCartUpdate);
		document.removeEventListener('click', this._onRemoveClick);
		clearInterval(this._pollInterval);
		// Clean up debounce timer
		if (this.logic.debounceTimer) {
			clearTimeout(this.logic.debounceTimer);
		}
		// Clean up navigation guard
		if (this.logic._beforeUnloadHandler) {
			window.removeEventListener('beforeunload', this.logic._beforeUnloadHandler);
		}
	}

	static get observedAttributes() { return ['cart-subtotal']; }
	attributeChangedCallback(name) { if (name === 'cart-subtotal') this.logic.onCartChange(); }
}

customElements.define('cart-gifts', CartGifts);
